import $ from "jquery";
import type JQuery from "jquery";
window.jQuery = jQuery;
import "jquery-ui";
import "../vendor/jquery.balloon";
import { GraphForm } from "./graph_form";
import { Keyword } from "./keyword";
import { StatsGraph } from "./stats_graph";
import { Series, SeriesValue } from "../model/series";
import { DataDate } from "../model/data_date";

// Interface definitions
interface TabQuery {
  type: string;
  partition: string;
  dimension: string;
  keyword: string;
  year: string;
  [key: string]: string;
}

interface SeriesResponse {
  stats: SeriesValue[];
  name: string;
  [key: string]: any;
}

interface AjaxErrorResponse {
  status: number;
  statusText?: string;
}

interface TabChangeHandler {
  (tab: Tab): void;
}

interface JQueryWithBalloon extends JQuery {
  showBalloon: any;
  hideBalloon: any;
}

class ComparisonGraphForm extends GraphForm {
  private keyword: Keyword;
  private tabs: TabPanel;
  private seriesCaches: SeriesCache;
  private missingKeywords: string[] = [];

  constructor() {
    super();
    this.keyword = new Keyword($("#keyword"));
    this.tabs = new TabPanel();
    this.seriesCaches = new SeriesCache();
  }

  // Element getters
  private targetElement(): JQuery {
    return $("#target");
  }

  private typeElement(): JQueryWithBalloon {
    return $("#type") as JQueryWithBalloon;
  }

  private partitionElement(): JQuery {
    return $("#partition");
  }

  private typePartitionElements(): JQuery {
    return this.typeElement().add(this.partitionElement());
  }

  private canvasElement(): JQuery {
    return $("#comparison_graph");
  }

  private dimensionElement(): JQuery {
    return $("#target_dimension");
  }

  private errorMsgElements(): JQuery {
    return $(".tab_error_message");
  }

  private formElement(): JQuery {
    return $("#comparisons_form");
  }

  private diagramTitleElement(): JQuery {
    return $(".diagram_title");
  }

  private csvButtonElement(): JQuery {
    return $(".comparison_graph_box .csv_button");
  }

  private additionalSynonymsElement(): JQuery {
    return $("#additional_synonyms");
  }

  private duplicatedSynonymsElement(): JQuery {
    return $("#duplicated_synonyms");
  }

  private keywordMessagesElement(): JQuery {
    return $("#tab_keyword_messages");
  }

  private missingKeywordsElement(): JQuery {
    return $("#tab_keyword_messages li.missed_keyword");
  }

  private duplicatedSynonymsMessageElement(): JQuery {
    return $("#tab_keyword_messages li.duplicated_synonyms");
  }

  private keywordTargetElement(): JQuery {
    return $("#keyword_target");
  }

  private areaTargetElement(): JQuery {
    return $("#area_target");
  }

  private categoryTargetElement(): JQuery {
    return $("#category_target");
  }

  // Getters and setters
  private getType(): string {
    return this.typeElement().val() as string;
  }

  private getPartition(): string {
    return this.partitionElement().val() as string;
  }

  private setTarget(value: string): void {
    this.targetElement().val(value);
  }

  private getTarget(): string {
    return this.targetElement().val() as string;
  }

  private getKeywordTarget(): string {
    return this.keywordTargetElement().val() as string;
  }

  private getAreaTarget(): string {
    return this.areaTargetElement().val() as string;
  }

  private getCategoryTarget(): string {
    return this.categoryTargetElement().val() as string;
  }

  private getAjaxUrl(): string {
    return this.canvasElement().data("ajaxUrl");
  }

  initialize(): void {
    this.keyword.initialize();
    this.tabs.initialize();
    this.seriesCaches.initialize(this.tabs.allKeys());
    this.missingKeywords = [];

    $("#tabs").tabs({
      active: this.tabs.position(this.dimensionElement().val() as string),
      activate: (event, ui) => {
        const tab = this.tabs.lookUp(ui.newPanel.index() - 1);
        this.initializeSelectedTab(tab);
        this.checkTypePartitionCombination();
        if (!this.isInvalidTypePartitionCombination()) {
          this.reRenderCurrentTab(tab);
        }
      },
    });

    this.initializeEventHandlers();
    this.forceSelectedTabHasAnyItem();

    const selectedTab = this.tabs.selected();
    selectedTab.setItemAttributes(selectedTab.hasMaxCheckedItems());
    this.disableYearlyQuery(selectedTab.key());
    this.validateSelectedTabHasAnyItem(selectedTab);
    this.showAdditionalContents(selectedTab.key());
  }

  private initializeSelectedTab(tab: Tab): void {
    tab.setItemAttributes(tab.hasMaxCheckedItems());
    this.validateSelectedTabHasAnyItem(tab);
    const tabName = tab.key();
    this.dimensionElement().val(tabName);
    this.disableYearlyQuery(tabName);
    this.showAdditionalContents(tabName);
  }

  private initializeEventHandlers(): void {
    const tabName = this.tabs.selected().key();

    this.keywordTargetElement().change(() => {
      this.setTarget(this.getKeywordTarget());
      this.seriesCaches.clear(tabName);
    });

    this.areaTargetElement().change(() => {
      this.setTarget(this.getAreaTarget());
      this.seriesCaches.clear(tabName);
    });

    this.categoryTargetElement().change(() => {
      this.setTarget(this.getCategoryTarget());
      this.seriesCaches.clear(tabName);
    });

    this.initializeSubmitEventHandler();
    this.initializeTypePartitionCombination();
  }

  private initializeSubmitEventHandler(): void {
    this.formElement().submit(() => {
      const tab = this.tabs.selected();
      if (tab.hasAnyCheckedItems()) {
        return !this.isInvalidTypePartitionCombination();
      } else {
        this.errorMsgElements().show();
        return false;
      }
    });
  }

  private initializeTypePartitionCombination(): void {
    this.typePartitionElements().change(() => {
      this.checkTypePartitionCombination();
      this.seriesCaches.clearAll();
    });
  }

  private checkTypePartitionCombination(): void {
    if (this.isInvalidTypePartitionCombination()) {
      this.typePartitionElements().addClass("invalid_type_partition");
      this.typeElement().showBalloon({
        contents: "このデータと集計期間の組み合わせは選択できません",
        classname: "error_balloon",
        css: null,
        showAnimation: function (d: number) {
          this.fadeIn(d);
        },
      });
    } else {
      this.typePartitionElements().removeClass("invalid_type_partition");
      this.typeElement().hideBalloon();
    }
  }

  private isInvalidTypePartitionCombination(): boolean {
    switch (this.getType()) {
      case "sv":
        return this.getPartition() === "yearly";
      default:
        return false;
    }
  }

  private forceSelectedTabHasAnyItem(): void {
    this.tabs.onTabItemChange((tab: Tab) => {
      this.validateSelectedTabHasAnyItem(tab);
      this.tabs.get(tab.key()).setItemAttributes(tab.hasMaxCheckedItems());
      this.seriesCaches.clear(tab.key());
      this.showAdditionalContents(tab.key());
    });
  }

  private validateSelectedTabHasAnyItem(tab: Tab): void {
    if (tab.hasAnyCheckedItems()) {
      this.errorMsgElements().hide();
    }
  }

  private disableYearlyQuery(tab: string): void {
    switch (tab) {
      case "year":
        this.partitionElement().children("option:last-child").remove();
        break;
      default:
        if (this.partitionElement().children().length === 2) {
          this.partitionElement().append('<option value="yearly">年</option>');
        }
        break;
    }
  }

  private showAdditionalContents(tab: string): void {
    switch (tab) {
      case "keyword":
        this.additionalSynonymsElement().show();
        if (this.hasDuplicatedSynonyms()) {
          this.duplicatedSynonymsElement().show();
          this.renderDuplicatedSynonymsMessage();
        }
        if (this.hasMissingKeywords()) {
          this.missingKeywordsElement().show();
        } else {
          this.missingKeywordsElement().hide();
        }
        break;
      default:
        this.additionalSynonymsElement().hide();
        this.duplicatedSynonymsElement().hide();
        this.missingKeywordsElement().hide();
        break;
    }
  }

  private hasDuplicatedSynonyms(): boolean {
    return (
      (this.duplicatedSynonymsElement().data("num-duplicated-synonyms") || 0) >
      0
    );
  }

  private hasMissingKeywords(): boolean {
    return this.missingKeywords.length > 0;
  }

  private isYearlyQuery(): boolean {
    return this.getPartition() === "yearly";
  }

  private reRenderCurrentTab(tab: Tab): void {
    this.disableCsvButton(this.csvButtonElement());
    this.showIndicator();
    this.updateGraphTitle(tab);
    this.render();
  }

  private showIndicator(): void {
    this.canvasElement().html('<div class="indicator gr" />');
  }

  private updateGraphTitle(tab: Tab): void {
    let graphType: string;
    switch (this.getType()) {
      case "si":
        graphType = "SI値";
        break;
      case "sv":
        graphType = "季節指数";
        break;
      default:
        graphType = "データ";
        break;
    }
    this.diagramTitleElement().html(`${graphType}${tab.title()}比較グラフ`);
  }

  private disableCsvButton(element: JQuery): void {
    element.addClass("disabled enable_later");
    const img = element.find("a").html();
    element.append(img);
    element.children().remove("a");
    element.data("url", this.reBuildCsvOutputUrl());
  }

  private reBuildCsvOutputUrl(): string {
    const base = "/comparisons/dump.csv?";
    const params = $.param(this.formElement().serializeArray());
    return base.concat(params);
  }

  private renderKeywordNotFound(graph: StatsGraph): void {
    graph.drawMessage(`
      「${this.keyword.value()}」は見付かりませんでした。
      別の単語をお試しください。
    `);
    this.additionalSynonymsElement().hide();
  }

  private renderRequireStandardContract(graph: StatsGraph): void {
    graph.drawMessage(`
      Standard 契約が必要です。
    `);
    this.additionalSynonymsElement().hide();
  }

  private renderDuplicatedSynonymsMessage(): void {
    if (this.hasDuplicatedSynonyms()) {
      this.duplicatedSynonymsMessageElement().show();
    } else {
      this.duplicatedSynonymsMessageElement().hide();
    }
  }

  private renderMissingKeywords(): void {
    if (this.tabs.selected().key() === "keyword" && this.hasMissingKeywords()) {
      const escapedKeywords = this.missingKeywords.map((keyword) =>
        $("<div>").text(keyword).html(),
      );
      const words =
        "「" +
        escapedKeywords.join("」「") +
        "」は見つかりませんでした。別の単語をお試し下さい。";
      this.keywordMessagesElement()
        .append(`<li class="missed_keyword">${words}</li>`)
        .show();

      for (let index = 0; index <= 4; index++) {
        const textBox = $(`#tab_keyword_${index}`);
        if (this.isMissingKeyword(textBox.val() as string)) {
          textBox.addClass("missed_keyword");
        }
      }
    }
  }

  private isMissingKeyword(keyword: string): boolean {
    return this.missingKeywords.indexOf(keyword) >= 0;
  }

  private resetMissingKeywords(tabName: string): void {
    if (tabName === "keyword") {
      this.missingKeywords = [];
      this.missingKeywordsElement().remove();
      $(".tab_item_keyword").removeClass("missed_keyword");
    }
  }

  private render(): void {
    const g = StatsGraph;
    const graph = new StatsGraph(this.canvasElement(), {
      xAxis: g.AxisPolicy.forPartition(this.getPartition()),
      yAxis: g.FloatAxis,
      legend: g.TextLegend,
    });

    const tab = this.tabs.selected();
    const tabName = tab.key();

    if (!tab.hasEnableContract()) {
      this.renderRequireStandardContract(graph);
      return;
    }

    if (this.keyword.isNotFound()) {
      this.renderKeywordNotFound(graph);
      return;
    }

    const caches = this.seriesCaches.get(tabName);
    if (caches.length > 0) {
      caches.forEach((series) => {
        graph.addSeries(series);
      });

      if (graph.hasNoData()) {
        this.renderNoDataMessage(graph);
        return;
      }

      graph.drawAll();
      this.enableCsvButton(this.csvButtonElement());
      return;
    }

    this.resetMissingKeywords(tabName);

    const dfd = $.Deferred();

    this.getTabQueries()
      .reduce((promise, query) => {
        return promise.then(() => {
          return this.fetchSeries(this.getAjaxUrl(), query).then(
            (series: Series) => {
              if (series.isEmpty()) {
                this.missingKeywords.push(series.name);
              } else {
                graph.addSeries(series);
                this.seriesCaches.addSeries(tabName, series);
              }
            },
          );
        });
      }, dfd.promise())
      .then(
        () => {
          switch (true) {
            case graph.isEmpty():
              graph.drawErrorMessage(`
              比較条件を選択して下さい
            `);
              break;
            case graph.hasNoData():
              this.renderNoDataMessage(graph);
              break;
            default:
              graph.drawAll();
              break;
          }
        },
        (res: AjaxErrorResponse) => {
          const currentDate = new Date().toLocaleString("ja-JP");
          graph.drawErrorMessage(`
          申し訳ありません、通信エラーが発生しました。
          少し時間をおいて再度お試しください。
          ステータス: ${res.status}, ${currentDate}
        `);
        },
      )
      .then(() => {
        this.enableCsvButton(this.csvButtonElement());
        this.renderMissingKeywords();
      });

    dfd.resolve();
  }

  private renderNoDataMessage(graph: StatsGraph): void {
    graph.drawMessage(`
      この条件ではデータが存在しません。
      期間や集計単位を変更してお試しください。
    `);
  }

  private fetchSeries(url: string, params: TabQuery): Promise<Series> {
    return Promise.resolve(
      $.ajax({
        url: url,
        data: params,
        dataType: "json",
      }),
    ).then((res: SeriesResponse) => {
      const origin = this.decideSeriesOrigin(params.year);
      return new Series(res.stats, res.name, origin);
    });
  }

  private decideSeriesOrigin(year: string): DataDate {
    if (this.isYearlyQuery()) {
      return new DataDate(window.tablemill.FIRST_DATA_DATE, "yearly");
    } else {
      return new DataDate(new Date(parseInt(year, 10), 0, 1), "yearly");
    }
  }

  private getTabQueries(): TabQuery[] {
    const tab = this.tabs.selected();
    const template: TabQuery = {
      type: this.getType(),
      partition: this.getPartition(),
      dimension: tab.key(),
      keyword: this.keyword.value(),
      year: this.getTarget(),
    };

    return this.tabs.selectedValues().map((value) => {
      const diff: { [key: string]: string } = {};
      diff[tab.key()] = value;
      return { ...template, ...diff };
    });
  }
}

// SeriesCache class
class SeriesCache {
  private caches: { [key: string]: Series[] } = {};

  initialize(tabs: string[]): void {
    tabs.forEach((tab) => {
      this.caches[tab] = [];
    });
  }

  addSeries(tab: string, series: Series): void {
    this.caches[tab].push(series);
  }

  get(tab: string): Series[] {
    return this.caches[tab] || [];
  }

  clear(tab: string): void {
    this.caches[tab] = [];
  }

  clearAll(): void {
    const keys = Object.keys(this.caches);
    this.initialize(keys);
  }
}

// TabPanel class
class TabPanel {
  private tabs: Tab[];
  private index: { [key: string]: Tab } = {};
  private tabPositions: { [key: string]: number } = {};
  private tabChangeHandlers: TabChangeHandler[] = [];

  constructor() {
    this.tabs = [
      new TabText("keyword", "キーワード"),
      new TabCheckbox("year", "年度"),
      new TabCheckbox("area", "地域"),
      new TabCheckbox("category", "年代・性別"),
    ];

    this.tabs.forEach((tab, index) => {
      this.index[tab.key()] = tab;
      this.tabPositions[tab.key()] = index;
    });
  }

  initialize(): void {
    this.enableTabChangeHook();
  }

  private enableTabChangeHook(): void {
    this.tabs.forEach((tab) => {
      tab.headElement().change(() => {
        this.runTabChangeHandlers(tab);
      });
    });
  }

  private runTabChangeHandlers(tab: Tab): void {
    this.tabChangeHandlers.forEach((handler) => handler(tab));
  }

  selected(): Tab {
    const key =
      $(".ui-tabs-active").attr("aria-controls")?.replace("tab_", "") || "";
    return this.index[key];
  }

  selectedValues(): string[] {
    return this.selected().values();
  }

  get(key: string): Tab {
    return this.index[key];
  }

  position(tab: string): number {
    return this.tabPositions[tab] || 0;
  }

  lookUp(pos: number): Tab {
    return this.tabs[pos];
  }

  onTabChange(handler: TabChangeHandler): void {
    this.tabChangeHandlers.push(handler);
  }

  onTabItemChange(handler: TabChangeHandler): void {
    this.tabs.forEach((tab) => {
      tab.onItemChange(() => handler(tab));
    });
  }

  allKeys(): string[] {
    return this.tabs.map((tab) => tab.key());
  }
}

// Abstract Tab class
abstract class Tab {
  protected tab: string;
  protected tab_title: string;

  constructor(tab: string, title: string) {
    this.tab = tab;
    this.tab_title = title;
  }

  key(): string {
    return this.tab;
  }

  title(): string {
    return this.tab_title;
  }

  protected value(): JQuery {
    return $(`.tab_item_${this.tab}`);
  }

  headElement(): JQuery {
    return $(`#tab_${this.tab}`);
  }

  elements(): JQuery {
    return $(`.tab_item_${this.tab}`);
  }

  numActiveValues(): number {
    return Object.keys(this.values()).length;
  }

  isSelected(): boolean {
    return !this.headElement().attr("aria-hidden");
  }

  setItemAttributes(shouldBeDisabled: boolean): void {
    this.elements().each((_, elem) => {
      const parentElem = $(elem).parent();
      if ($(elem).prop("checked")) {
        parentElem.addClass("checked");
      } else {
        parentElem.removeClass("checked");
        $(elem).prop("disabled", shouldBeDisabled);
        if (shouldBeDisabled) {
          parentElem.addClass("disabled");
        } else {
          parentElem.removeClass("disabled");
        }
      }
    });
  }

  onItemChange(handler: () => void): void {
    this.elements().change(handler);
  }

  hasEnableContract(): boolean {
    return this.headElement().find(".require_upper_contract").length === 0;
  }

  abstract values(): string[];
  abstract hasMaxCheckedItems(): boolean;
  abstract hasAnyCheckedItems(): boolean;
}

// TabText class
class TabText extends Tab {
  values(): string[] {
    const values = this.elements()
      .map((_, elem) => $.trim(($(elem).val() as string) || ""))
      .get()
      .filter((val) => val !== "");

    return [$(`#${this.tab}`).val() as string].concat(values);
  }

  hasMaxCheckedItems(): boolean {
    return false;
  }

  hasAnyCheckedItems(): boolean {
    return true;
  }
}

// TabCheckbox class
class TabCheckbox extends Tab {
  private static readonly SELECTION_UPPER_LIMIT = 12;
  private static readonly SELECTION_LOWER_LIMIT = 1;

  hasMaxCheckedItems(): boolean {
    return this.numActiveValues() === TabCheckbox.SELECTION_UPPER_LIMIT;
  }

  hasAnyCheckedItems(): boolean {
    return this.numActiveValues() >= TabCheckbox.SELECTION_LOWER_LIMIT;
  }

  values(): string[] {
    return this.elements()
      .filter((_, elem) => $(elem).prop("checked"))
      .map((_, elem) => $(elem).val() as string)
      .get();
  }
}

// Global export
window.tablemill = window.tablemill || {};
window.tablemill.ComparisonGraphForm = ComparisonGraphForm;

export { ComparisonGraphForm };
